import React from "react";
import { resolveAssetUrl } from "@/lib/api";
import { getLetterhead } from "@/lib/letterheads";
import { rupeesInWords } from "@/lib/receiptWords";

// Payment Receipt / Payment Voucher card, A5 landscape (210 x 148 mm), in the DanRock design.
export const RECEIPT_W = 210; // mm
export const RECEIPT_H = 148; // mm

export const RECEIPT_CSS = `
.rc-sheet, .rc-sheet * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.rc-sheet { font-family: 'Montserrat', Arial, Helvetica, sans-serif; color: #1f2328; background: #fff; width: ${RECEIPT_W}mm; height: ${RECEIPT_H}mm; position: relative; overflow: hidden; }
.rc-abs { position: absolute; }
.rc-line { border-bottom: 0.3mm solid #4a4f55; }
`;

const GOLD_DEFAULT = "#C49A22";

const PinIcon = ({ c }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill={c}><path d="M12 2C8.1 2 5 5.1 5 9c0 5.3 7 13 7 13s7-7.7 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" /></svg>
);
const CircleIcon = ({ bg, children }) => (
  <span style={{ width: "5.6mm", height: "5.6mm", borderRadius: "50%", background: bg, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{children}</span>
);
const PhoneG = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" /></svg>
);
const MailG = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></svg>
);
const GlobeG = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" /></svg>
);

const Watermark = () => (
  <div className="rc-abs" style={{ left: "160mm", top: "66mm", width: "46mm", zIndex: 0 }}>
    <svg viewBox="0 0 200 204" width="100%">
      <path fill="#F6F2E9" fillRule="evenodd" d="M0 0 H98 C160 0 196 40 196 98 C196 126 186 148 170 164 H0 Z M44 36 V128 H128 C140 118 152 108 152 96 C152 60 130 36 92 36 Z" />
      <path fill="#F6F2E9" stroke="#FFFFFF" strokeWidth="9" paintOrder="stroke" d="M0 204 L110 98 L200 204 H166 L110 146 L46 204 Z" />
    </svg>
  </div>
);

const fmtAmount = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return "";
  const whole = Math.abs(x - Math.round(x)) < 0.005;
  return `₹ ${x.toLocaleString("en-IN", { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 })}/-`;
};

const dateParts = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? { d: m[3], m: m[2], y: m[1] } : { d: "", m: "", y: "" };
};

// one ruled field: label followed by a line that carries the value
function Field({ label, value, left, top, width, fontSize = "10.5pt", bold = false, labelSize = "10.5pt" }) {
  return (
    <div className="rc-abs" style={{ left: `${left}mm`, top: `${top}mm`, width: `${width}mm`, display: "flex", alignItems: "flex-end", zIndex: 1 }}>
      <span style={{ fontSize: labelSize, whiteSpace: "nowrap", lineHeight: 1, paddingBottom: "0.8mm" }}>{label}</span>
      <div className="rc-line" style={{ flex: 1, marginLeft: "2.2mm", minHeight: "6.4mm", display: "flex", alignItems: "flex-end", overflow: "hidden" }}>
        <span style={{ fontSize, fontWeight: bold ? 700 : 500, lineHeight: 1.15, paddingBottom: "0.7mm", paddingLeft: "1mm", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{value}</span>
      </div>
    </div>
  );
}

export default function ReceiptDocument({ receipt, profile }) {
  const lh = getLetterhead(profile);
  const accent = lh.accent || GOLD_DEFAULT;
  const isVoucher = receipt.kind === "voucher";
  const title = isVoucher ? "PAYMENT VOUCHER" : "PAYMENT RECEIPT";
  const partyLabel = isVoucher ? "Paid To:" : "Received From:";
  const dp = dateParts(receipt.date);
  const words = rupeesInWords(receipt.amount);
  const mode = receipt.mode || "";
  const modeLabel = mode === "Cash" || mode === "Cheque" ? "Cash / Cheque No.:" : "Mode / Ref. No.:";
  const modeValue = mode === "Cash" && !receipt.mode_ref ? "Cash" : [mode, receipt.mode_ref].filter(Boolean).join(" – ");
  const party = receipt.party_name || "";
  const purpose = receipt.purpose || "";
  const purposeSize = purpose.length > 190 ? "8pt" : purpose.length > 120 ? "9pt" : "10.5pt";
  const sigUrl = receipt.show_signature_image && profile?.signature_url ? resolveAssetUrl(profile.signature_url) : "";
  const brand = lh.brandName || lh.companyName;
  const LINE_H = 10.5; // mm between the two "For" lines

  return (
    <div className="rc-sheet" id="receipt-document-root" data-testid="receipt-document">
      {lh.key === "danrock" && <Watermark />}

      {/* ---------- header ---------- */}
      {lh.logo ? (
        <img src={lh.logo} alt="" className="rc-abs" style={{ left: "11.5mm", top: "7.5mm", width: "57mm", zIndex: 1 }} />
      ) : (
        <div className="rc-abs" style={{ left: "11.5mm", top: "10mm", width: "100mm", fontFamily: "Georgia, serif", fontWeight: 700, fontSize: "20pt", color: accent, lineHeight: 1.15, zIndex: 1 }}>{brand}</div>
      )}
      <div className="rc-abs" style={{ left: "123mm", top: "8mm", width: "0.3mm", height: "19mm", background: "#8a8f96", zIndex: 1 }} />
      <div className="rc-abs" style={{ left: "128mm", top: "8mm", width: "71.6mm", zIndex: 1, lineHeight: 1.4 }}>
        <div style={{ fontWeight: 700, fontSize: "12pt", color: "#15181c" }}>{lh.logo ? brand : lh.companyName}</div>
        {lh.divisionLine && <div style={{ fontSize: "7.8pt", color: "#2a2f35" }}>{lh.divisionLine}</div>}
        {lh.addressLines.length > 0 && (
          <div style={{ display: "flex", gap: "1.6mm", marginTop: "2.2mm", alignItems: "flex-start" }}>
            <span style={{ paddingTop: "0.5mm", flex: "none" }}><PinIcon c="#6b7077" /></span>
            <span style={{ fontSize: "8.2pt", color: "#2a2f35" }}>
              {lh.addressLines.map((l, i) => (<span key={i} style={{ display: "block" }}>{l}</span>))}
            </span>
          </div>
        )}
      </div>
      <div className="rc-abs" style={{ left: "11.5mm", right: "10.4mm", top: "32mm", height: "0.55mm", background: accent, zIndex: 1 }} />

      {/* ---------- title, number, date ---------- */}
      <div className="rc-abs" style={{ left: "11.5mm", top: "37.5mm", fontWeight: 700, fontSize: "20pt", letterSpacing: "0.01em", color: "#1b1f24", zIndex: 1, lineHeight: 1.1 }}>{title}</div>
      <Field label="No." value={receipt.receipt_no} left={11.5} top={50} width={52} />
      <div className="rc-abs" style={{ right: "10.4mm", top: "46.5mm", display: "flex", alignItems: "flex-end", gap: "1.5mm", fontSize: "10.5pt", zIndex: 1 }}>
        <span style={{ lineHeight: 1, paddingBottom: "0.8mm", marginRight: "0.5mm" }}>Date:</span>
        {[[dp.d, 11], [dp.m, 11], [dp.y, 16]].map(([val, w], i) => (
          <React.Fragment key={i}>
            {i > 0 && <span style={{ lineHeight: 1, paddingBottom: "0.8mm" }}>/</span>}
            <span className="rc-line" style={{ width: `${w}mm`, minHeight: "6.4mm", display: "inline-flex", alignItems: "flex-end", justifyContent: "center", fontWeight: 500, paddingBottom: "0.7mm" }}>{val}</span>
          </React.Fragment>
        ))}
      </div>

      {/* ---------- body ---------- */}
      <Field label={partyLabel} value={party} left={11.5} top={64} width={188.1} fontSize={party.length > 70 ? "8.5pt" : "11pt"} bold />
      <Field label="Amount:" value={fmtAmount(receipt.amount)} left={11.5} top={74} width={90} fontSize="11.5pt" bold />
      <Field label={modeLabel} value={modeValue} left={105} top={74} width={94.6} fontSize={modeValue.length > 40 ? "8.5pt" : "10pt"} />
      {words && (
        <div className="rc-abs" style={{ left: "29mm", top: "81.3mm", width: "75mm", fontSize: "7.4pt", fontStyle: "italic", color: "#555", lineHeight: 1.25, zIndex: 1 }}>
          ({words})
        </div>
      )}

      <div className="rc-abs" style={{ left: "11.5mm", top: "90mm", width: "148.5mm", height: `${LINE_H * 2}mm`, zIndex: 1 }}>
        <div className="rc-abs" style={{ left: 0, top: 0, right: 0, bottom: 0, backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent ${LINE_H - 0.3}mm, #4a4f55 ${LINE_H - 0.3}mm, #4a4f55 ${LINE_H}mm)` }} />
        <div className="rc-abs" style={{ left: 0, top: 0, lineHeight: 1, fontSize: "10.5pt", paddingTop: `${LINE_H - 5.4}mm` }}>For:</div>
        <div className="rc-abs" style={{ left: 0, right: 0, top: "1.8mm", height: `${LINE_H * 2 - 1.8}mm`, overflow: "hidden", fontSize: purposeSize, fontWeight: 500, lineHeight: `${LINE_H}mm`, textIndent: "10.5mm", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{purpose}</div>
      </div>

      {/* ---------- signatures ---------- */}
      <div className="rc-abs" style={{ left: "11.5mm", top: "115mm", fontSize: "10.5pt", zIndex: 1, lineHeight: 1 }}>{isVoucher ? "Received by" : "Received by"}</div>
      {!isVoucher && sigUrl && <img src={sigUrl} alt="" className="rc-abs" style={{ left: "12mm", top: "114mm", height: "12mm", zIndex: 1, mixBlendMode: "multiply" }} />}
      <div className="rc-abs rc-line" style={{ left: "11.5mm", top: "127mm", width: "44mm", height: 0, zIndex: 1 }} />
      <div className="rc-abs" style={{ left: "11.5mm", top: "128.4mm", fontSize: "7.5pt", color: "#555", zIndex: 1, lineHeight: 1 }}>{isVoucher ? "(Payee's Signature)" : "(Authorized Signatory)"}</div>
      {isVoucher ? (
        <>
          <div className="rc-abs" style={{ left: "105mm", top: "115mm", fontSize: "10.5pt", zIndex: 1, lineHeight: 1 }}>Authorised by</div>
          {sigUrl && <img src={sigUrl} alt="" className="rc-abs" style={{ left: "105.5mm", top: "114mm", height: "12mm", zIndex: 1, mixBlendMode: "multiply" }} />}
          <div className="rc-abs rc-line" style={{ left: "105mm", top: "127mm", width: "44mm", height: 0, zIndex: 1 }} />
          <div className="rc-abs" style={{ left: "105mm", top: "128.4mm", fontSize: "7.5pt", color: "#555", zIndex: 1, lineHeight: 1 }}>(Authorized Signatory)</div>
        </>
      ) : (
        <div className="rc-abs" style={{ left: "105mm", top: "115mm", fontSize: "10.5pt", zIndex: 1, lineHeight: 1 }}>Stamp</div>
      )}

      {/* ---------- footer ---------- */}
      <div className="rc-abs" style={{ left: "11.5mm", right: "10.4mm", top: "134mm", height: "0.3mm", background: accent, zIndex: 1 }} />
      <div className="rc-abs" style={{ left: "11.5mm", top: "137.2mm", display: "flex", alignItems: "center", fontSize: "9pt", color: "#4b5057", zIndex: 1 }}>
        {lh.phone && (<span style={{ display: "inline-flex", alignItems: "center", gap: "2.2mm" }}><CircleIcon bg={accent}><PhoneG /></CircleIcon>{lh.phone}</span>)}
        {lh.phone && lh.email && <span style={{ display: "inline-block", width: "0.3mm", height: "5mm", background: accent, margin: "0 6mm" }} />}
        {lh.email && (<span style={{ display: "inline-flex", alignItems: "center", gap: "2.2mm" }}><CircleIcon bg={accent}><MailG /></CircleIcon>{lh.email}</span>)}
        {lh.website && <span style={{ display: "inline-block", width: "0.3mm", height: "5mm", background: accent, margin: "0 6mm" }} />}
        {lh.website && (<span style={{ display: "inline-flex", alignItems: "center", gap: "2.2mm" }}><CircleIcon bg={accent}><GlobeG /></CircleIcon>{lh.website}</span>)}
      </div>
    </div>
  );
}
